<script setup lang="ts">
import {
  atIndexMaybe,
  isJust,
  isNothing,
  just,
  nothing,
  toDomAttr,
  type Maybe,
} from "../../lib/maybe";
import { computed, nextTick, onUnmounted, useTemplateRef, watch } from "vue";

const props = defineProps<{
  open: boolean;
  /** `bottom` is a full-width sheet (narrow layout, ZW-276). */
  side: "left" | "right" | "bottom";
  closeOnBackdrop: boolean;
  zIndex: number;
  /** DOM id for aria-controls on external triggers. */
  id: Maybe<string>;
  /** id of the element that labels this dialog. */
  labelledBy: Maybe<string>;
  /** Accessible name when there is no labelling element. */
  label: Maybe<string>;
  /** Width of the panel (CSS length). Ignored for `side="bottom"`. */
  width: string;
  /** Height of a `side="bottom"` sheet (CSS length). */
  height: string;
  /** Focus the first matching selector after open. */
  initialFocus: string;
  /** Drop inset padding so a full-height child (e.g. outline) can fill the panel. */
  flush: boolean;
  /** Edge-to-edge panel (mobile account sheet). Ignores `width`. */
  fullBleed: boolean;
}>();

const emit = defineEmits<{
  close: [];
  opened: [];
  closed: [];
}>();

const panel = useTemplateRef<HTMLElement>("panel");

const panelTransition = computed(() => `zw-flyout-${props.side}`);

const panelStyle = computed(() =>
  props.side === "bottom"
    ? { zIndex: props.zIndex + 1, width: "100%", height: props.height }
    : {
        zIndex: props.zIndex + 1,
        width: props.fullBleed ? "100%" : props.width,
      },
);

let escapeListener: Maybe<(event: KeyboardEvent) => void> = nothing;
/** Element focused before the panel opened; focus returns there on close. */
let returnFocus: Maybe<HTMLElement> = nothing;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [contenteditable="true"], [tabindex]:not([tabindex="-1"])';

function focusables(root: HTMLElement): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) =>
      el.tabIndex >= 0 &&
      el.getClientRects().length > 0 &&
      getComputedStyle(el).visibility === "visible" &&
      el.closest("[inert]") === null,
  );
}

/** Keep Tab inside the open panel (modal drawer). */
function trapTab(event: KeyboardEvent): void {
  const root = panel.value;
  if (root === null) return;
  const items = focusables(root);
  if (items.length === 0) {
    event.preventDefault();
    return;
  }
  // WebKit can skip buttons/links during native Tab navigation, so its next
  // target need not match the first/last elements in this list. Own each move
  // to keep keyboard focus inside the modal on every platform.
  event.preventDefault();
  const activeIndex = items.findIndex(
    (item) => item === document.activeElement,
  );
  const nextIndex = event.shiftKey
    ? (activeIndex <= 0 ? items.length : activeIndex) - 1
    : (activeIndex + 1) % items.length;
  const next = atIndexMaybe(items, nextIndex);
  if (isJust(next)) next.value.focus();
}

function rememberFocus(): void {
  // Safari does not focus buttons on pointer clicks. Prefer the declared
  // trigger so closing a drawer still returns focus to the control that opens it.
  const root = panel.value;
  const active =
    root !== null && root.id !== ""
      ? (document.querySelector(`[aria-controls="${CSS.escape(root.id)}"]`) ??
        document.activeElement)
      : document.activeElement;
  returnFocus =
    active instanceof HTMLElement && active !== document.body
      ? just(active)
      : nothing;
}

/** Return focus to the trigger unless something outside took it already. */
function restoreFocus(): void {
  const target = returnFocus;
  returnFocus = nothing;
  if (isNothing(target) || target.value.isConnected === false) return;
  const active = document.activeElement;
  const root = panel.value;
  const lost =
    active === null ||
    active === document.body ||
    (root !== null && root.contains(active));
  if (lost) target.value.focus();
}

function onPanelAfterLeave(): void {
  emit("closed");
}

function onBackdropClick(): void {
  if (props.closeOnBackdrop) emit("close");
}

function onPanelKeydown(event: KeyboardEvent): void {
  if (event.key === "Escape") {
    event.preventDefault();
    emit("close");
    return;
  }
  if (event.key === "Tab") trapTab(event);
}

function focusInitial(): void {
  const root = panel.value;
  if (root === null) return;
  for (const selector of props.initialFocus.split(",")) {
    const trimmed = selector.trim();
    if (trimmed === "") continue;
    const el = root.querySelector<HTMLElement>(trimmed);
    if (el !== null) {
      el.focus();
      return;
    }
  }
}

function bindEscape(): void {
  unbindEscape();
  escapeListener = just((event: KeyboardEvent) => {
    if (event.key !== "Escape") return;
    event.preventDefault();
    emit("close");
  });
  document.addEventListener("keydown", escapeListener.value);
}

function unbindEscape(): void {
  if (isJust(escapeListener) === false) return;
  document.removeEventListener("keydown", escapeListener.value);
  escapeListener = nothing;
}

watch(
  () => props.open,
  async (open) => {
    if (open === false) {
      unbindEscape();
      restoreFocus();
      return;
    }
    rememberFocus();
    bindEscape();
    await nextTick();
    focusInitial();
    emit("opened");
  },
);

onUnmounted(() => {
  unbindEscape();
  if (props.open) restoreFocus();
});

defineExpose({
  el: panel,
  focus: focusInitial,
});
</script>

<template>
  <Teleport to="body">
    <Transition name="zw-flyout-backdrop">
      <div
        v-if="open"
        class="zw-flyout-backdrop"
        :style="{ zIndex }"
        role="presentation"
        @click="onBackdropClick"
      />
    </Transition>
    <Transition :name="panelTransition" @after-leave="onPanelAfterLeave">
      <aside
        v-show="open"
        :id="toDomAttr(id)"
        ref="panel"
        class="zw-flyout-panel zw-paper-surface"
        :class="{
          'zw-flyout-panel--flush': flush,
          'zw-flyout-panel--full-bleed': fullBleed,
        }"
        :data-side="side"
        :style="panelStyle"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="toDomAttr(labelledBy)"
        :aria-label="toDomAttr(label)"
        @click.stop
        @keydown="onPanelKeydown"
      >
        <slot />
      </aside>
    </Transition>
  </Teleport>
</template>

<style scoped>
.zw-flyout-backdrop {
  position: fixed;
  inset: 0;
  background: color-mix(in oklch, var(--text-strong) 18%, transparent);
}

.zw-flyout-panel {
  position: fixed;
  top: 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  --zw-flyout-inset: clamp(1.25rem, 4vw, 1.75rem);
  padding: var(--space-3xl) var(--zw-flyout-inset) 0;
  background: var(--bg);
  overflow: auto;
}

.zw-flyout-panel > :deep(*) {
  flex: 1;
  min-height: 0;
}

.zw-flyout-panel--flush {
  padding: 0;
  overflow: hidden;
}

.zw-flyout-panel--full-bleed {
  border: 0;
  padding-top: max(var(--space-3xl), env(safe-area-inset-top, 0px));
  padding-bottom: env(safe-area-inset-bottom, 0px);
  padding-left: max(var(--zw-flyout-inset), env(safe-area-inset-left, 0px));
  padding-right: max(var(--zw-flyout-inset), env(safe-area-inset-right, 0px));
}

.zw-flyout-panel--full-bleed[data-side="left"],
.zw-flyout-panel--full-bleed[data-side="right"] {
  border: 0;
}

.zw-flyout-panel[data-side="left"] {
  left: 0;
  border-right: 1px solid var(--line);
}

.zw-flyout-panel[data-side="right"] {
  right: 0;
  border-left: 1px solid var(--line);
}

.zw-flyout-panel[data-side="bottom"] {
  top: auto;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 100dvh;
  border-top: 1px solid var(--line);
  border-radius: 0.75rem 0.75rem 0 0;
  box-shadow: 0 -8px 24px
    color-mix(in oklch, var(--text-strong) 12%, transparent);
}

.zw-flyout-backdrop-enter-active,
.zw-flyout-backdrop-leave-active {
  transition: opacity var(--duration-med) var(--ease-out-quart);
}

.zw-flyout-backdrop-enter-from,
.zw-flyout-backdrop-leave-to {
  opacity: 0;
}

.zw-flyout-left-enter-active,
.zw-flyout-left-leave-active,
.zw-flyout-right-enter-active,
.zw-flyout-right-leave-active,
.zw-flyout-bottom-enter-active,
.zw-flyout-bottom-leave-active {
  transition: transform var(--duration-med) var(--ease-out-quart);
}

.zw-flyout-bottom-enter-from,
.zw-flyout-bottom-leave-to {
  transform: translateY(100%);
}

.zw-flyout-left-enter-from,
.zw-flyout-left-leave-to {
  transform: translateX(-100%);
}

.zw-flyout-right-enter-from,
.zw-flyout-right-leave-to {
  transform: translateX(100%);
}

@media (prefers-reduced-motion: reduce) {
  .zw-flyout-backdrop-enter-active,
  .zw-flyout-backdrop-leave-active,
  .zw-flyout-left-enter-active,
  .zw-flyout-left-leave-active,
  .zw-flyout-right-enter-active,
  .zw-flyout-right-leave-active,
  .zw-flyout-bottom-enter-active,
  .zw-flyout-bottom-leave-active {
    transition: none;
  }
}
</style>

<style>
/* Safe-area for flush drawers on the mobile shell only. */
html[data-zw-mobile-shell="1"] .zw-flyout-panel.zw-flyout-panel--flush {
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  padding-left: env(safe-area-inset-left, 0px);
  padding-right: env(safe-area-inset-right, 0px);
}
</style>
